/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// The header cart icon (`#cart-icon-bubble`) is a real Shopify section
// (cart-icon-bubble.liquid) but it's rendered inline inside header.liquid's
// own markup rather than through a section group, so it has no live
// Section Rendering API id to refresh via fetch - that's why it used to get
// stuck showing a bare item count with no icon after an add. We mirror its
// two possible icon states here (copied verbatim from the theme's
// icon-cart.svg / icon-cart-empty.svg) and update it with plain DOM calls
// instead, using the real `cart.item_count` we already fetched.
const CART_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-cart" viewBox="0 0 512 512"><path fill="currentColor" d="M 202.4 1.4 c -20.5 4.7 -37.6 18 -46.4 36 -6.7 13.6 -7 16.1 -7 51.3 l 0 31.3 -47.4 0 c -46.4 0 -47.4 0 -50 2.1 l -2.6 2 0 126.9 c 0 111.7 0.2 127 1.5 128.4 2.9 2.9 6.7 2.9 11.7 0.2 0.2 0 0.5 -55 0.8 -122.1 l 0.5 -122 42.5 0 42.5 0 0.5 23.2 c 0.5 21.7 0.6 23.3 2.5 24.7 2.6 2 6.4 2 9 0 1.9 -1.4 2 -3 2.5 -24.7 l 0.5 -23.2 52.5 0 52.5 0 0.5 23.2 c 0.5 21.7 0.6 23.3 2.5 24.7 2.6 2 6.4 2 9 0 1.9 -1.4 2 -3 2.5 -24.7 l 0.5 -23.2 42.5 0 42.5 0 0.3 180.7 0.2 180.8 -153 0 -153 0 0 -47.9 c 0 -52.1 0.2 -50.3 -5.5 -51.7 -2.3 -0.6 -6.4 1 -7.7 3 -0.4 0.6 -0.8 25 -0.8 54.3 l 0 53.2 2.2 2.1 c 2.2 2 2.3 2 204.1 2 204.2 0 204.7 0 206.9 -3.4 0.4 -0.6 0.8 -87.4 0.8 -192.8 l 0 -191.7 -2.6 -2 c -2.6 -2.1 -3.6 -2.1 -50 -2.1 l -47.4 0 0 -31.3 c 0 -35.3 -0.3 -37.7 -7.1 -51.6 -3.3 -6.6 -6 -10.3 -12.3 -16.6 -13.8 -13.9 -28 -20 -46.6 -20 -14.4 0 -27 3.5 -37.1 10.2 l -3.9 2.6 -3.7 -2.5 c -5 -3.3 -12.3 -6.5 -19.2 -8.3 -8.2 -2.2 -23.4 -2.8 -30.7 -1.1 z m 23.8 14.7 c 5.9 1.3 17.8 6.3 17.8 7.6 0 0.4 -1.5 2.9 -3.3 5.5 -1.9 2.6 -4.6 7.3 -6 10.5 -5.3 11.9 -5.7 15.2 -5.7 49 l 0 31.3 -33.1 0 -33.1 0 0.5 -30.8 c 0.4 -25.2 0.8 -31.7 2.2 -36.1 8.2 -26.1 35 -42.4 60.7 -37 z m 81.7 0.4 c 17.6 4.2 33 18.7 38.6 36.6 1.4 4.4 1.8 10.9 2.2 36.1 l 0.5 30.8 -33.1 0 -33.1 0 0 -31.3 c 0 -33.8 -0.4 -37.1 -5.7 -49 -1.4 -3.2 -4.1 -7.9 -6 -10.5 -1.8 -2.6 -3.3 -5.1 -3.3 -5.5 0 -1.2 10.6 -5.9 16.5 -7.3 6.9 -1.7 15.8 -1.6 23.4 0.1 z m -48.9 21.1 c 1.3 2 3.3 5.5 4.3 7.8 4.7 10.2 4.9 11.9 5.4 43.8 l 0.5 30.8 -13.2 0 -13.2 0 0.5 -30.3 c 0.5 -31.3 0.9 -34.6 5.4 -44.3 2 -4.4 6.5 -11.4 7.3 -11.4 0.3 0 1.6 1.6 3 3.6 z m 149.8 260.4 l 0.2 162.5 -12.2 12.2 c -6.8 6.8 -12.6 12.3 -13 12.3 -1 0 -1.1 -348.4 -0.1 -349.4 0.4 -0.3 6.1 -0.5 12.7 -0.4 l 12.1 0.3 0.3 162.5 z m 40 12.2 c 0.1 96.2 -0.1 174.8 -0.5 174.8 -0.4 0 -6.3 -5.5 -13 -12.2 l -12.3 -12.3 0 -162.1 c 0 -89.1 0.3 -162.4 0.7 -162.8 0.4 -0.3 6.1 -0.5 12.7 -0.4 l 12.1 0.3 0.3 174.7 z m -21.8 176.3 l 10.5 10.5 -21.5 0 -21.5 0 10.5 -10.5 c 5.8 -5.8 10.7 -10.5 11 -10.5 0.3 0 5.2 4.7 11 10.5 z"></path></svg>';
const CART_ICON_EMPTY_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-cart-empty" viewBox="0 0 512 512"><path fill="currentColor" d="M 202.4 1.4 c -20.5 4.7 -37.6 18 -46.4 36 -6.7 13.6 -7 16.1 -7 51.3 l 0 31.3 -47.4 0 c -46.4 0 -47.4 0 -50 2.1 l -2.6 2 0 126.9 c 0 111.7 0.2 127 1.5 128.4 2.9 2.9 6.7 2.9 11.7 0.2 0.2 0 0.5 -55 0.8 -122.1 l 0.5 -122 42.5 0 42.5 0 0.5 23.2 c 0.5 21.7 0.6 23.3 2.5 24.7 2.6 2 6.4 2 9 0 1.9 -1.4 2 -3 2.5 -24.7 l 0.5 -23.2 52.5 0 52.5 0 0.5 23.2 c 0.5 21.7 0.6 23.3 2.5 24.7 2.6 2 6.4 2 9 0 1.9 -1.4 2 -3 2.5 -24.7 l 0.5 -23.2 42.5 0 42.5 0 0.3 180.7 0.2 180.8 -153 0 -153 0 0 -47.9 c 0 -52.1 0.2 -50.3 -5.5 -51.7 -2.3 -0.6 -6.4 1 -7.7 3 -0.4 0.6 -0.8 25 -0.8 54.3 l 0 53.2 2.2 2.1 c 2.2 2 2.3 2 204.1 2 204.2 0 204.7 0 206.9 -3.4 0.4 -0.6 0.8 -87.4 0.8 -192.8 l 0 -191.7 -2.6 -2 c -2.6 -2.1 -3.6 -2.1 -50 -2.1 l -47.4 0 0 -31.3 c 0 -35.3 -0.3 -37.7 -7.1 -51.6 -3.3 -6.6 -6 -10.3 -12.3 -16.6 -13.8 -13.9 -28 -20 -46.6 -20 -14.4 0 -27 3.5 -37.1 10.2 l -3.9 2.6 -3.7 -2.5 c -5 -3.3 -12.3 -6.5 -19.2 -8.3 -8.2 -2.2 -23.4 -2.8 -30.7 -1.1 z m 23.8 14.7 c 5.9 1.3 17.8 6.3 17.8 7.6 0 0.4 -1.5 2.9 -3.3 5.5 -1.9 2.6 -4.6 7.3 -6 10.5 -5.3 11.9 -5.7 15.2 -5.7 49 l 0 31.3 -33.1 0 -33.1 0 0.5 -30.8 c 0.4 -25.2 0.8 -31.7 2.2 -36.1 8.2 -26.1 35 -42.4 60.7 -37 z m 81.7 0.4 c 17.6 4.2 33 18.7 38.6 36.6 1.4 4.4 1.8 10.9 2.2 36.1 l 0.5 30.8 -33.1 0 -33.1 0 0 -31.3 c 0 -33.8 -0.4 -37.1 -5.7 -49 -1.4 -3.2 -4.1 -7.9 -6 -10.5 -1.8 -2.6 -3.3 -5.1 -3.3 -5.5 0 -1.2 10.6 -5.9 16.5 -7.3 6.9 -1.7 15.8 -1.6 23.4 0.1 z m -48.9 21.1 c 1.3 2 3.3 5.5 4.3 7.8 4.7 10.2 4.9 11.9 5.4 43.8 l 0.5 30.8 -13.2 0 -13.2 0 0.5 -30.3 c 0.5 -31.3 0.9 -34.6 5.4 -44.3 2 -4.4 6.5 -11.4 7.3 -11.4 0.3 0 1.6 1.6 3 3.6 z m 149.8 260.4 l 0.2 162.5 -12.2 12.2 c -6.8 6.8 -12.6 12.3 -13 12.3 -1 0 -1.1 -348.4 -0.1 -349.4 0.4 -0.3 6.1 -0.5 12.7 -0.4 l 12.1 0.3 0.3 162.5 z m 40 12.2 c 0.1 96.2 -0.1 174.8 -0.5 174.8 -0.4 0 -6.3 -5.5 -13 -12.2 l -12.3 -12.3 0 -162.1 c 0 -89.1 0.3 -162.4 0.7 -162.8 0.4 -0.3 6.1 -0.5 12.7 -0.4 l 12.1 0.3 0.3 174.7 z m -21.8 176.3 l 10.5 10.5 -21.5 0 -21.5 0 10.5 -10.5 c 5.8 -5.8 10.7 -10.5 11 -10.5 0.3 0 5.2 4.7 11 10.5 z"></path></svg>';

function updateHeaderCartIcon(itemCount) {
  const iconLink = document.getElementById('cart-icon-bubble');
  if (!iconLink) return;
  const svgWrapper = iconLink.querySelector('.svg-wrapper');
  if (svgWrapper) svgWrapper.innerHTML = itemCount > 0 ? CART_ICON_SVG : CART_ICON_EMPTY_SVG;

  let bubble = iconLink.querySelector('.cart-count-bubble');
  if (itemCount > 0) {
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      iconLink.appendChild(bubble);
    }
    const shown = itemCount < 100 ? String(itemCount) : '';
    bubble.innerHTML =
      (shown ? '<span aria-hidden="true">' + shown + '</span>' : '') +
      '<span class="visually-hidden">' + itemCount + '</span>';
  } else if (bubble) {
    bubble.remove();
  }
}

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Keep the drawer/notification's empty-state styling in sync with the
  // real cart, independent of whatever any other subscriber did with it.
  document.querySelectorAll('cart-drawer, cart-notification').forEach((el) => {
    el.classList.toggle('is-empty', !(cartData && cartData.item_count > 0));
    });
  updateHeaderCartIcon(cartData ? cartData.item_count : 0);

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

// Safety net: some cart mutations elsewhere in the theme (Dawn's default
// add-to-cart submit handlers among them) publish 'cart-update' with the raw
// /cart/add.js line-item response instead of a full cart object. Subscribers
// like quick-add-bulk.js and cart-drawer.js expect `cartData.items` /
// `cartData.sections` to exist and choke on the incomplete shape - that
// is the source of "cart drawer looks empty" / "header icon turns into a
// bare number" bugs right after adding a product. Catch those here and do a
// full, correct refresh instead.
let dawnCartGuardBusy = false;
subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
  if (!event || event.source === 'external-refresh' || dawnCartGuardBusy) return;
  const cartData = event.cartData;
  const looksComplete = cartData && Array.isArray(cartData.items) && cartData.sections;
  if (looksComplete) return;
  dawnCartGuardBusy = true;
  refreshDawnCartUI().finally(function () {
    dawnCartGuardBusy = false;
  });
});
